import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FinancialTable from './FinancialTable';
import DataChart, { parseTableString } from './DataChart';
import EnhancedLink from './EnhancedLink';

/**
 * ResponseRenderer
 * 
 * Intelligent renderer that splits a raw message string into sections:
 * - Markdown Text (Heading, Paragraph, Lists) -> Rendered via ReactMarkdown
 * - Financial Tables (Pipe-formatted blocks) -> Rendered via FinancialTable
 * 
 * It preserves the order of content.
 */
const ResponseRenderer = ({ content, isDarkMode, isExport = false }) => {
    const [expandedTables, setExpandedTables] = useState(new Set());

    const toggleTable = (idx) => {
        setExpandedTables(prev => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            return next;
        });
    };

    // Determine whether a table string has enough numeric data to warrant a chart
    const hasChartData = (tableString) => {
        const data = parseTableString(tableString);
        if (!data || !data.rows || data.rows.length === 0) return false;

        const isNumeric = (val) => {
            if (val === null || val === undefined) return false;
            return /[-+]?(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?|\.\d+)/.test(String(val));
        };

        let numericCols = [];
        for (let c = 1; c < data.headers.length; c++) {
            const numericCount = data.rows.filter((r) => isNumeric(r[c])).length;
            if (numericCount > 0) numericCols.push(c);
        }

        // Single-row transposed table heuristic
        const isSingleRow = data.rows.length === 1;
        if (isSingleRow && isNumeric(data.rows[0][0]) && data.headers.length >= 3) {
            const hasDateLikeHeader = data.headers.some((h) => /year|date|time|period|id/i.test(h));
            if (!hasDateLikeHeader) {
                numericCols = [];
                for (let c = 0; c < data.headers.length; c++) {
                    if (isNumeric(data.rows[0][c])) numericCols.push(c);
                }
            }
        }

        if (numericCols.length === 0) return false;
        const hasMultipleNumericCols = numericCols.length >= 2;
        if (isSingleRow && hasMultipleNumericCols) return true;
        if (data.rows.length >= 1 && numericCols.length >= 1) return true;
        return false;
    };

    // Some reports present forecasts as bullets instead of a markdown table.
    // Convert a contiguous run of top-level numeric bullets into a temporary
    // table so it can use the same bar-chart renderer while preserving the
    // original prose below it.
    const getNumericListChart = (text) => {
        const numberPattern = /[-+]?(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?|\.\d+)/;
        const lines = String(text).split('\n');
        let run = [];
        const flush = () => {
            if (run.length < 3) {
                run = [];
                return null;
            }
            const rows = run.map(({ label, value }) => `| ${label} | ${value} |`);
            const result = `| Metric | Value |\n| --- | --- |\n${rows.join('\n')}`;
            run = [];
            return result;
        };

        for (const line of lines) {
            const match = line.match(/^[-*]\s+(.+)$/);
            if (!match) {
                const result = flush();
                if (result) return result;
                continue;
            }
            const value = match[1].match(numberPattern);
            if (!value) {
                const result = flush();
                if (result) return result;
                continue;
            }
            const label = match[1]
                .replace(/\|/g, '/')
                .replace(/\*\*/g, '')
                .slice(0, 90)
                .trim();
            run.push({ label, value: value[0] });
        }
        return flush();
    };

    if (!content) return null;

    // Escape accidental ordered list markers from hard-wrapped LLM output.
    // 4+ digit numbers (years) get their dot escaped to avoid becoming list items.
    // 1–2 digit lines that look like report section headers (e.g. "1. Market Overview")
    // are promoted to ## headings so they don't render below their own sub-sections.
    const sanitizedContent = content
        .replace(/^(\d{4,})\.(\s)/gm, '$1\\.$2')
        // Split form: number alone on one line, title on the next (e.g. "1.\nMarket Overview")
        .replace(/^(\d{1,2})\.\s*\n([A-Z][^\n]{2,})/gm, '## $1. $2')
        // Inline form: "1. Market Overview" — only promote lines that don't end with sentence punctuation
        .replace(/^(\d{1,2})\. ([A-Z][^\n]*[^.!?,\s])$/gm, '## $1. $2');

    // Split content by identifying table blocks
    // Regex looks for blocks that look like tables:
    // Starts with a pipe, has multiple pipe lines, bounded by newlines
    const sections = [];
    const lines = sanitizedContent.split('\n');

    let currentBlockType = 'text'; // 'text' or 'table'
    let currentChunkLines = [];

    const normalizeTableLine = (line) => line.replace(/^\s*[-*]\s+(?=\|)/, '').trim();
    const isTableNoiseLine = (line) => /^[\s]*[-*•·▪◦]+[\s]*$/.test(String(line).trim());

    const isTableLine = (line) => {
        const trimmed = normalizeTableLine(line);
        return trimmed.startsWith('|') && trimmed.indexOf('|', 1) !== -1;
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (isTableLine(line)) {
            if (currentBlockType === 'text') {
                // Switch from text to table
                if (currentChunkLines.length > 0) {
                    sections.push({ type: 'text', content: currentChunkLines.join('\n') });
                    currentChunkLines = [];
                }
                currentBlockType = 'table';
            }
            currentChunkLines.push(normalizeTableLine(line));
        } else {
            if (currentBlockType === 'table') {
                // Potential break in table. 
                // Check if it's just an empty line or noise.
                // If it's an empty line, IGNORE IT. Keep the table block open.
                // This effectively merges tables separated by whitespace.

                const cleanLine = line.trim();
                if (cleanLine === '' || isTableNoiseLine(line)) {
                    // Skip empty lines to maintain table contiguity
                    continue;
                }

                // If we are here, it's a non-empty, non-table line. 
                // Close the table block.

                // Switch from table to text
                if (currentChunkLines.length > 0) {
                    // Check if it's a valid table (sanity check, e.g. at least 2 lines)
                    if (currentChunkLines.length >= 2) {
                        sections.push({ type: 'table', content: currentChunkLines.join('\n') });
                    } else {
                        // Not a real table, treat as text
                        // But since we are ignoring empty lines now, orphan rows might end up here if they are really text.
                        sections.push({ type: 'text', content: currentChunkLines.join('\n') });
                    }
                    currentChunkLines = [];
                }
                currentBlockType = 'text';
            }
            currentChunkLines.push(line);
        }
    }

    // Push remaining chunk
    if (currentChunkLines.length > 0) {
        sections.push({ type: currentBlockType, content: currentChunkLines.join('\n') });
    }

    return (
        <div className={`response-renderer ${isExport ? 'space-y-6 text-[15px] leading-7' : 'space-y-4'} ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            {sections.map((section, idx) => {
                if (section.type === 'table') {
                    const tableHasChart = hasChartData(section.content);
                    const isExpanded = isExport || !tableHasChart ? true : expandedTables.has(idx);

                    return (
                        <div
                            key={idx}
                            className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-white'} ${isExport ? 'shadow-sm break-inside-avoid mb-6' : ''}`}
                        >
                            {/* Chart on top (only when data permits) */}
                            {tableHasChart && (
                                <DataChart
                                    tableString={section.content}
                                    isDarkMode={isDarkMode}
                                    isExport={isExport}
                                />
                            )}

                            {/* Collapsible toggle only when there's a chart */}
                            {tableHasChart && !isExport ? (
                                <>
                                    <button
                                        onClick={() => toggleTable(idx)}
                                        className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700/50' : 'text-slate-500 hover:bg-slate-50'}`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            View Data Table
                                        </span>
                                        <svg
                                            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {isExpanded && (
                                        <div className="px-4 pb-4">
                                            <FinancialTable
                                                tableString={section.content}
                                                isDarkMode={isDarkMode}
                                                isExport={isExport}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={`${isExport ? 'px-5 pb-5 pt-4' : 'px-4 pb-4 pt-2'}`}>
                                    <FinancialTable
                                        tableString={section.content}
                                        isDarkMode={isDarkMode}
                                        isExport={isExport}
                                    />
                                </div>
                            )}
                        </div>
                    );
                } else {
                    const numericListChart = getNumericListChart(section.content);
                    return (
                        <div key={idx} className={`markdown-section ${isExport ? 'mb-6 break-inside-avoid' : ''}`}>
                            {numericListChart && (
                                <DataChart
                                    tableString={numericListChart}
                                    isDarkMode={isDarkMode}
                                    isExport={isExport}
                                />
                            )}
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ node, ...props }) => <EnhancedLink url={props.href} isDarkMode={isDarkMode} />,
                                    // We deliberately do NOT override 'table' here because we pulled tables out.
                                    // However, if small tables slip through regex, we fallback to default style or simple override.
                                    table: ({ node, ...props }) => (
                                        <div className={`my-4 ${isExport ? '' : 'overflow-x-auto'}`}>
                                            <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'} border rounded-lg ${isExport ? 'table-fixed' : ''}`} {...props} />
                                        </div>
                                    ),
                                    thead: ({ node, ...props }) => (
                                        <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} {...props} />
                                    ),
                                    th: ({ node, ...props }) => (
                                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} {...props} />
                                    ),
                                    tbody: ({ node, ...props }) => (
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`} {...props} />
                                    ),
                                    tr: ({ node, ...props }) => (
                                        <tr className={isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} {...props} />
                                    ),
                                    td: ({ node, ...props }) => (
                                        <td className={`px-4 py-3 text-sm ${isExport ? 'align-top break-words whitespace-normal' : 'whitespace-nowrap'} ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`} {...props} />
                                    ),
                                    p: ({ node, ...props }) => <p className={`last:mb-0 leading-relaxed ${isExport ? 'mb-3 text-[15px]' : 'mb-2'}`} {...props} />,
                                    h1: ({ node, children, ...props }) => <h1 className={`font-bold ${isExport ? 'text-3xl mt-8 mb-5 text-slate-900' : 'text-2xl mt-6 mb-4'}`} {...props}>{children}</h1>,
                                    h2: ({ node, children, ...props }) => <h2 className={`font-bold ${isExport ? 'text-2xl mt-7 mb-4 text-slate-900' : 'text-xl mt-5 mb-3'}`} {...props}>{children}</h2>,
                                    h3: ({ node, children, ...props }) => <h3 className={`font-bold ${isExport ? 'text-xl mt-6 mb-3 text-slate-900' : 'text-lg mt-4 mb-2'}`} {...props}>{children}</h3>,
                                    h4: ({ node, children, ...props }) => <h4 className={`font-bold ${isExport ? 'text-lg mt-5 mb-3 text-slate-900' : 'text-base mt-3 mb-2'}`} {...props}>{children}</h4>,
                                    h5: ({ node, children, ...props }) => <h5 className={`font-bold ${isExport ? 'text-base mt-4 mb-2 text-slate-900' : 'text-sm mt-2 mb-1'}`} {...props}>{children}</h5>,
                                    h6: ({ node, children, ...props }) => <h6 className={`font-bold uppercase tracking-wide ${isExport ? 'text-sm mt-4 mb-2 text-slate-700' : 'text-xs mt-2 mb-1'}`} {...props}>{children}</h6>,
                                    ul: ({ node, ...props }) => <ul className={`list-disc list-outside space-y-1.5 pl-6 ${isExport ? 'mb-5 text-[15px]' : 'mb-4'}`} {...props} />,
                                    ol: ({ node, ...props }) => <ol className={`list-decimal list-outside space-y-1.5 pl-6 ${isExport ? 'mb-5 text-[15px]' : 'mb-4'}`} {...props} />,
                                    li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                                    pre: ({ node, ...props }) => (
                                        <pre className={`p-4 rounded-lg overflow-x-auto my-4 ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-800 text-gray-100'} ${isExport ? 'break-inside-avoid' : ''}`} {...props} />
                                    ),
                                    code: ({ node, inline, className, children, ...props }) => (
                                        <code
                                            className={!inline ? className : `px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} font-mono text-sm`}
                                            {...props}
                                        >
                                            {children}
                                        </code>
                                    ),
                                    blockquote: ({ node, ...props }) => (
                                        <blockquote className={`border-l-4 pl-4 italic my-4 ${isDarkMode ? 'border-gray-500 text-gray-400' : 'border-gray-300 text-gray-600'}`} {...props} />
                                    )
                                }}
                            >
                                {section.content}
                            </ReactMarkdown>
                        </div>
                    );
                }
            })}
        </div>
    );
};

export default ResponseRenderer;
